import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetIdentity, useNotify, useRefresh } from 'react-admin';
import Parse from 'parse';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography
} from '@mui/material';

const AdminOtpVerification = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { data: identity, isLoading } = useGetIdentity();

  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [emailLabel, setEmailLabel] = useState('');
  const [lastSentAt, setLastSentAt] = useState(null);

  useEffect(() => {
    if (isLoading) return;

    if (identity?.role !== 'Admin') {
      navigate('/operatorGames', { replace: true });
      return;
    }

    if (identity?.adminFirstLoginCompleted === true) {
      navigate('/operatorGames', { replace: true });
      return;
    }

    setEmailLabel(identity?.otpDeliveryEmail || identity?.email || '');
  }, [identity, isLoading, navigate]);

  const handleVerify = async () => {
    const code = otp.trim();
    if (code.length !== 6) {
      notify('Enter the 6-digit OTP sent to your email.', { type: 'warning' });
      return;
    }

    setLoading(true);
    try {
      await Parse.Cloud.run('verifyAdminLoginOtp', { otp: code });
      const currentUser = Parse.User.current();
      if (currentUser) {
        await currentUser.fetch();
      }
      await refresh();
      notify('Email OTP verified successfully.', { type: 'success' });
      navigate('/operatorGames', { replace: true });
    } catch (error) {
      notify(error?.message || 'OTP verification failed.', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      const result = await Parse.Cloud.run('requestAdminLoginOtp');
      if (result?.maskedEmail) {
        setEmailLabel(result.maskedEmail);
      }
      setLastSentAt(new Date());
      notify('A new OTP has been sent to your email.', { type: 'success' });
    } catch (error) {
      notify(error?.message || 'Failed to resend OTP.', { type: 'error' });
    } finally {
      setResendLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#1A1A1A',
          backgroundImage: 'url(/Assets/Images/login.svg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          p: 2,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.74) 100%)'
          }
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#FFC107', mb: 2 }} />
          <Typography sx={{ color: '#fff', fontWeight: 600, letterSpacing: 0.3 }}>
            Loading verification screen...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#1A1A1A',
        backgroundImage: 'url(/Assets/Images/login.svg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        p: 2
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.58) 0%, rgba(0,0,0,0.72) 100%)'
        }}
      />
      <Card
        sx={{
          width: '100%',
          maxWidth: 500,
          position: 'relative',
          zIndex: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.88)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 193, 7, 0.14)',
          borderRadius: '16px',
          boxShadow: '0 24px 80px rgba(0, 0, 0, 0.55)'
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                component="img"
                src="/Assets/Logos/sidebar-logo.svg"
                alt="Vegas Empire"
                sx={{
                  height: 42,
                  width: 'auto',
                  mb: 2
                }}
              />
              <Typography sx={{ color: '#FFC107', fontSize: 26, fontWeight: 800, mb: 1 }}>
                Email OTP Verification
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.74)', fontSize: 14, lineHeight: 1.6 }}>
                Enter the 6-digit code sent to <strong>{emailLabel || 'your email'}</strong>.
              </Typography>
            </Box>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

            <Stack spacing={1.5}>
              <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 13 }}>
                OTP Code
              </Typography>
              <TextField
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                placeholder="Enter 6-digit OTP"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#2B2B2B',
                    color: '#fff',
                    borderRadius: '10px',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                    '&.Mui-focused fieldset': { borderColor: '#FFC107' }
                  },
                  '& .MuiOutlinedInput-input': {
                    letterSpacing: '0.45em',
                    textAlign: 'center',
                    fontSize: '18px',
                    fontWeight: 700
                  }
                }}
              />
            </Stack>

            <Stack spacing={1.5}>
              <Button
                onClick={handleVerify}
                disabled={loading || otp.trim().length !== 6}
                variant="contained"
                sx={{
                  backgroundColor: '#FFC107',
                  color: '#000',
                  fontWeight: 800,
                  textTransform: 'none',
                  py: 1.3,
                  borderRadius: '10px',
                  '&:hover': { backgroundColor: '#FFD54F' }
                }}
              >
                {loading ? <CircularProgress size={20} sx={{ color: '#000' }} /> : 'Verify OTP'}
              </Button>

              <Button
                onClick={handleResend}
                disabled={resendLoading}
                variant="outlined"
                sx={{
                  borderColor: 'rgba(255,255,255,0.22)',
                  color: '#fff',
                  textTransform: 'none',
                  py: 1.3,
                  borderRadius: '10px',
                  '&:hover': {
                    borderColor: 'rgba(255,193,7,0.45)',
                    backgroundColor: 'rgba(255,193,7,0.06)'
                  }
                }}
              >
                {resendLoading ? (
                  <CircularProgress size={18} sx={{ color: '#fff' }} />
                ) : (
                  'Resend OTP'
                )}
              </Button>
            </Stack>

            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, textAlign: 'center' }}>
              {lastSentAt
                ? `Last OTP sent at ${lastSentAt.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}`
                : 'The code expires in 5 minutes.'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminOtpVerification;
